import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, ADMIN_ROLES, useAuth } from "@/context/AuthContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import Home from "@/pages/Home";
import TrackPage from "@/pages/TrackPage";
import CmsPage from "@/pages/CmsPage";
import AboutUs from "@/pages/AboutUs";
import Support from "@/pages/Support";
import BlogPost from "@/pages/BlogPost";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Register from "@/pages/Register";
import CustomerLayout from "@/pages/customer/CustomerLayout";
import Dashboard from "@/pages/customer/Dashboard";
import BookShipment from "@/pages/customer/BookShipment";
import IntlBooking from "@/pages/customer/IntlBooking";
import Shipments from "@/pages/customer/Shipments";
import ShipmentDetail from "@/pages/customer/ShipmentDetail";
import Ndr from "@/pages/customer/Ndr";
import Wallet from "@/pages/customer/Wallet";
import RatesPage from "@/pages/customer/RatesPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminShipments from "@/pages/admin/AdminShipments";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCommissions from "@/pages/admin/AdminCommissions";
import AdminCouriers from "@/pages/admin/AdminCouriers";
import AdminCms from "@/pages/admin/AdminCms";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminIntlRates from "@/pages/admin/AdminIntlRates";
import AdminProfit from "@/pages/admin/AdminProfit";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminCrm from "@/pages/admin/AdminCrm";
import AdminNdr from "@/pages/admin/AdminNdr";
import CrmPanel from "@/pages/crm/CrmPanel";
import CCare from "@/pages/ccare/CCare";
import PickupPanel from "@/pages/pickup/PickupPanel";
import BillPage from "@/pages/BillPage";
import PickupRequest from "@/pages/PickupRequest";
import Shop from "@/pages/Shop";
import Media from "@/pages/Media";
import MediaHub from "@/pages/MediaHub";
import Branches from "@/pages/Branches";
import PartnerWithUs from "@/pages/PartnerWithUs";
import DomesticCourier from "@/pages/DomesticCourier";
import InternationalCourier from "@/pages/InternationalCourier";
import EcommerceShipping from "@/pages/EcommerceShipping";
import BusinessCourier from "@/pages/BusinessCourier";
import NriCourier from "@/pages/NriCourier";
import RateCalculatorPage from "@/pages/RateCalculatorPage";
import Complaint from "@/pages/Complaint";
import Careers from "@/pages/Careers";
import LegalCenter from "@/pages/LegalCenter";
import ProhibitedPage from "@/pages/ProhibitedPage";
import NewsHub from "@/pages/NewsHub";
import NewsArticle from "@/pages/NewsArticle";
import FaqCenter from "@/pages/FaqCenter";
import Orders from "@/pages/customer/Orders";
import AdminShop from "@/pages/admin/AdminShop";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminBranches from "@/pages/admin/AdminBranches";
import AdminPartnerEnquiries from "@/pages/admin/AdminPartnerEnquiries";
import AdminCareers from "@/pages/admin/AdminCareers";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminNews from "@/pages/admin/AdminNews";
import AdminApiAccess from "@/pages/admin/AdminApiAccess";
import InstallPrompt from "@/components/InstallPrompt";
import AiAssistant from "@/components/ai/AiAssistant";
import AiDashboard from "@/pages/admin/ai/AiDashboard";
import AiChats from "@/pages/admin/ai/AiChats";
import AiLeads from "@/pages/admin/ai/AiLeads";
import AiKnowledge from "@/pages/admin/ai/AiKnowledge";
import AiSettings from "@/pages/admin/ai/AiSettings";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={ADMIN_ROLES.includes(user.role) ? "/admin" : "/app"} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/track" element={<TrackPage />} />
            <Route path="/track/:awb" element={<TrackPage />} />
            <Route path="/ccare" element={<CCare />} />
            <Route path="/bill/:awb" element={<BillPage />} />
            <Route path="/p/careers" element={<Careers />} />
            <Route path="/p/terms" element={<LegalCenter slug="terms" />} />
            <Route path="/p/privacy" element={<LegalCenter slug="privacy" />} />
            <Route path="/p/refund" element={<LegalCenter slug="refund" />} />
            <Route path="/p/shipping" element={<LegalCenter slug="shipping" />} />
            <Route path="/p/prohibited" element={<ProhibitedPage />} />
            <Route path="/p/news" element={<NewsHub />} />
            <Route path="/p/news/:slug" element={<NewsArticle />} />
            <Route path="/p/faq" element={<FaqCenter />} />
            <Route path="/p/faqs" element={<Navigate to="/p/faq" replace />} />
            <Route path="/faq" element={<Navigate to="/p/faq" replace />} />
            <Route path="/p/about" element={<AboutUs />} />
            <Route path="/p/:slug" element={<CmsPage />} />
            <Route path="/support" element={<Support />} />
            <Route path="/pickup-request" element={<PickupRequest />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/media" element={<Navigate to="/media-videos" replace />} />
            <Route path="/media-videos" element={<MediaHub />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="/partner-with-us" element={<PartnerWithUs />} />
            <Route path="/domestic-courier" element={<DomesticCourier />} />
            <Route path="/international-courier" element={<InternationalCourier />} />
            <Route path="/ecommerce-shipping" element={<EcommerceShipping />} />
            <Route path="/business-courier" element={<BusinessCourier />} />
            <Route path="/nri-courier" element={<NriCourier />} />
            <Route path="/rate-calculator" element={<RateCalculatorPage />} />
            <Route path="/complaint" element={<Complaint />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/login" element={<Login />} />
            <Route path="/ai-admin" element={<AdminLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/crm" element={<Protected roles={["crm"]}><CrmPanel /></Protected>} />
            <Route path="/pickup" element={<Protected roles={["pickup"]}><PickupPanel /></Protected>} />
            <Route path="/app" element={<Protected><CustomerLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="book" element={<BookShipment />} />
              <Route path="book-international" element={<IntlBooking />} />
              <Route path="shipments" element={<Shipments />} />
              <Route path="ndr" element={<Ndr />} />
              <Route path="orders" element={<Orders />} />
              <Route path="shipments/:id" element={<ShipmentDetail />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="rates" element={<RatesPage />} />
            </Route>
            <Route path="/admin" element={<Protected roles={ADMIN_ROLES}><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="shipments" element={<AdminShipments />} />
              <Route path="ndr" element={<AdminNdr />} />
              <Route path="shop" element={<AdminShop />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="branches" element={<AdminBranches />} />
              <Route path="partner-enquiries" element={<AdminPartnerEnquiries />} />
              <Route path="careers" element={<AdminCareers />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="faqs" element={<AdminFaqs standalone />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="couriers" element={<AdminCouriers />} />
              <Route path="cms" element={<AdminCms />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="intl-rates" element={<AdminIntlRates />} />
              <Route path="api-access" element={<AdminApiAccess />} />
            <Route path="ai" element={<AiDashboard />} />
            <Route path="ai/chats" element={<AiChats />} />
            <Route path="ai/leads" element={<AiLeads />} />
            <Route path="ai/knowledge" element={<AiKnowledge />} />
            <Route path="ai/settings" element={<AiSettings />} />
              <Route path="profit" element={<AdminProfit />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="crm" element={<AdminCrm />} />
              <Route path="audit" element={<AdminAudit />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AiAssistant />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <ImpersonationBanner />
      </AuthProvider>
      <InstallPrompt />
    </div>
  );
}

export default App;
